import { normalizeRecord } from '@/features/timeline/libs/timeline-record-normalizer';
import type { TimelineRecord } from '@/features/timeline/model/timeline-record';

export interface OpenFileResult {
  record: TimelineRecord;
  fileName: string;
  handle: FileSystemFileHandle;
}

export interface SaveFileResult {
  fileName: string;
  handle: FileSystemFileHandle;
}

const filePickerTypes = [
  {
    description: 'Timeline',
    accept: { 'application/json': ['.timeline'] as const },
  },
];

export class TimelineFileService {
  async openFile(): Promise<OpenFileResult | null> {
    try {
      const [handle] = await window.showOpenFilePicker({ types: filePickerTypes });
      return this.readFromHandle(handle);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return null;
      }
      throw err;
    }
  }

  async openFromHandle(handle: FileSystemFileHandle): Promise<OpenFileResult | null> {
    const permission = await handle.requestPermission({ mode: 'readwrite' });
    if (permission !== 'granted') {
      return null;
    }
    return this.readFromHandle(handle);
  }

  async readFile(handle: FileSystemFileHandle): Promise<OpenFileResult> {
    return this.readFromHandle(handle);
  }

  private async readFromHandle(handle: FileSystemFileHandle): Promise<OpenFileResult> {
    const file = await handle.getFile();
    const text = await file.text();
    const parsed = JSON.parse(text);
    const record = normalizeRecord(parsed);
    if (!record) {
      throw new Error('無効なタイムラインファイルです');
    }
    return {
      record,
      fileName: file.name,
      handle,
    };
  }

  async saveFile(record: TimelineRecord, handle?: FileSystemFileHandle): Promise<SaveFileResult | null> {
    if (handle) {
      await this.writeToHandle(handle, record);
      return { fileName: handle.name, handle };
    }
    return this.saveFileAs(record);
  }

  async saveFileAs(record: TimelineRecord, suggestedName?: string): Promise<SaveFileResult | null> {
    try {
      const handle = await window.showSaveFilePicker({
        types: filePickerTypes,
        suggestedName: suggestedName || 'Untitled.timeline',
      });
      await this.writeToHandle(handle, record);
      return { fileName: handle.name, handle };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return null;
      }
      throw err;
    }
  }

  private async writeToHandle(handle: FileSystemFileHandle, record: TimelineRecord): Promise<void> {
    const output: TimelineRecord = {
      ...record,
      $schema: 'https://timeline-make.app/schema/2.0.json',
    };
    const writable = await handle.createWritable();
    try {
      await writable.write(JSON.stringify(output, null, 2));
    } finally {
      await writable.close();
    }
  }
}
